import {
    ComposedChart,
    Line,
    XAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Bar,
    YAxis
} from "recharts";
import { BarCharts, Filtro, GraphicsDomain, Variable_indicator } from "../../interfaces/Indicator";
import { useDataKeyDualAxis } from "../../hooks/useDataKeyDualAxis";
import CustomToolTip from "./CustomToolTip";
import { CHART_CONFIG, CHART_TYPES, ChartTypeValue } from '../../utils/chartTypes'
import { useChartDomains } from "../../hooks/useChartDomains";
import { forwardRef } from "react";
import NotDataDisplay from "../Cards/NotDataDisplay";
import { messages } from "../../utils/const";

interface IMultipleYAxesChart {
    data: BarCharts[],
    chartTypeLeft?: ChartTypeValue,
    variable_analisis_axis_y_left: string[],
    variable_analisis_axis_y_right?: Variable_indicator | null,
    rangeAxisYleft?: GraphicsDomain | null,
    rangeAxisYRight?: GraphicsDomain | null,
    errorsNoData: boolean;
    filters: Filtro[];
}

type ChartRenderType = 'bar' | 'stacked' | 'grouped' | 'line';

const CHART_TYPE_MAP: Record<number, ChartRenderType> = {
    [CHART_TYPES.GRAFICO_BARRA]: 'bar',
    [CHART_TYPES.GRAFICO_BARRA_APILABLE]: 'stacked',
    [CHART_TYPES.GRAFICO_BARRA_AGRUPADA]: 'grouped',
    [CHART_TYPES.GRAFICO_LINEAL]: 'line',
};

const getChartRenderType = (chartType?: number): ChartRenderType => {
    if (!chartType) return 'line';
    return CHART_TYPE_MAP[chartType] ?? 'line';
};

const renderChartElement = (
    chartType: ChartRenderType,
    key: string,
    color: string,
    yAxisId: string,
) => {
    const baseProps = { yAxisId, dataKey: key };

    const formatName = (key: string) => key.split('-').map(e => e.split(':')[1]).join('/') ?? '-';

    switch (chartType) {
        case 'bar':
        case 'grouped':
            return <Bar key={key} {...baseProps} fill={color} isAnimationActive={false} />;

        case 'stacked':
            return (
                <Bar
                    key={key}
                    {...baseProps}
                    name={formatName(key)}
                    stackId="a"
                    fill={color}
                    isAnimationActive={false}
                />
            );

        case 'line':
        default:
            return (
                <Line
                    key={key}
                    {...baseProps}
                    name={formatName(key)}
                    type="monotone"
                    stroke={color}
                    strokeWidth={2}
                    isAnimationActive={false}
                />
            );
    }
};

const MultipleYAxesChart = forwardRef<HTMLDivElement, IMultipleYAxesChart>(({
    data,
    chartTypeLeft,
    variable_analisis_axis_y_left,
    variable_analisis_axis_y_right,
    rangeAxisYleft,
    rangeAxisYRight,
    errorsNoData,
}, ref) => {

    const { domainYLeft, domainYRight } = useChartDomains(
        data,
        variable_analisis_axis_y_left,
        variable_analisis_axis_y_right?.titulo!,
        rangeAxisYleft!,
        rangeAxisYRight!
    );

    const { dataKey } = useDataKeyDualAxis(data, variable_analisis_axis_y_right ?? undefined);
    const chartRenderType = getChartRenderType(chartTypeLeft);

    if (!dataKey) return null;

    return (
        <>
            <ResponsiveContainer width="100%" height="100%" ref={ref}>
                <ComposedChart data={data} margin={CHART_CONFIG.MARGINS}>
                    <CartesianGrid strokeDasharray={CHART_CONFIG.STROKE_DASH} />
                    <XAxis
                        dataKey="name"
                        stroke={CHART_CONFIG.STROKE_COLOR}
                        tick={{ fontSize: CHART_CONFIG.TICK_FONT_SIZE }}
                    />
                    <YAxis
                        yAxisId="left"
                        orientation="left"
                        domain={domainYLeft}
                        ticks={rangeAxisYleft?.ticks}
                        stroke={CHART_CONFIG.STROKE_COLOR}
                        tick={{ fontSize: CHART_CONFIG.TICK_FONT_SIZE }}
                        allowDataOverflow={true}
                        allowDecimals={false}
                        interval={0}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={domainYRight}
                        ticks={rangeAxisYRight?.ticks}
                        stroke={CHART_CONFIG.STROKE_COLOR}
                        tick={{ fontSize: CHART_CONFIG.TICK_FONT_SIZE }}
                        allowDataOverflow={true}
                        interval={0}
                    />
                    <Tooltip
                        content={<CustomToolTip />}
                        wrapperStyle={{ pointerEvents: 'auto', zIndex: 50 }}
                        isAnimationActive={false}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: CHART_CONFIG.LEGEND_FONT_SIZE }}
                        align='center'
                    />
                    {dataKey.left.map(({ color, key }) =>
                        renderChartElement(chartRenderType, key, color, 'left')
                    )}

                    {dataKey.right.map(({ color, key }) =>
                        renderChartElement('line', key, color, 'right')
                    )}
                </ComposedChart>
            </ResponsiveContainer>
            {errorsNoData ? <NotDataDisplay message={messages.chart} /> : null}
        </>
    );
});

export default MultipleYAxesChart;